/**
 * 컬렉션 묶음 API (IA SC-012~SC-016) — 보유 위에 얹는 분류 계층.
 *
 * ⚠ 보유(`api/collection.ts`)와 축이 다르다. 보유는 "내가 가진 카드"이고 묶음은 "그것을
 *   어떻게 모아 두었나"다. 묶음을 지워도 보유는 남는다.
 */
import { apiGet, apiPost, apiPut, apiDelete } from "./client";
import type { HoldingCard } from "./collection";
import type { PriceView } from "./card";

export interface CollectionGroupItem {
  groupSerialNo: number;
  name: string;
  cardCount: number;
  /** 시세를 아는 카드들의 **누적** 합. 모르는 카드는 빠진다 — 그래서 valuedCount 를 함께 본다 */
  totalValue: number | null;
  /** 그 합에 들어간 카드 수 */
  valuedCount: number;
  /**
   * 30일 가치 변화율(%). 카드별 30일 추세로 역산한 값이고 총액 스냅샷이 아니다.
   * 추세를 아는 카드가 하나도 없으면 null — 그때는 화면이 아무 말도 하지 않는다
   * (0% 로 그리면 "안 변했다"가 되는데 실제로는 "모른다"다).
   */
  trendRate: number | null;
  /** 미리보기 썸네일 — **최근 담은 순** 상위 5장의 이미지 경로 */
  previewImages: string[];
  /**
   * 기본 컬렉션('즐겨찾기')인가 — 회원당 하나이고 **삭제·이름 변경이 막혀 있다**
   * (IA SC-015 · FN-084). 서버가 컬렉션 목록을 처음 내려줄 때 만들므로 목록은 절대
   * 비지 않는다 — 담기 시트가 "고를 컬렉션이 없다" 상태로 열리지 않는 근거다.
   */
  defaultGroup: boolean;
}

export interface CollectionGroupCard {
  mappingSerialNo: number;
  card: HoldingCard;
  /** 그 카드의 **일반판 대표 시세**. 내 보유 평가액이 아니다(묶음은 카드를 가리킨다). */
  currentPrice: PriceView;
  /** 실제로 보유 중인가 — 묶음에는 보유하지 않은 카드도 들어갈 수 있다(IA SC-016) */
  owned: boolean;
}

export interface CollectionGroupDetail {
  groupSerialNo: number;
  name: string;
  cardCount: number;
  /** 시세를 아는 카드들의 합 — 모르는 카드는 빠진다 */
  totalValue: number | null;
  /** 그 합에 들어간 카드 수. 화면이 "N장 기준" 을 밝힌다 */
  valuedCount: number;
  cards: CollectionGroupCard[];
  /** 기본 컬렉션인가 — 관리 메뉴가 삭제·이름 변경을 감춘다 (FN-084) */
  defaultGroup: boolean;
}

export function fetchCollectionGroups(): Promise<CollectionGroupItem[]> {
  return apiGet<CollectionGroupItem[]>("/collection/groups");
}

/**
 * 내 **모든** 컬렉션의 최근 담은 카드 — 홈 §3 (FN-049).
 *
 * 묶음을 가리지 않는다. 홈이 묻는 것은 "어느 컬렉션에 있나" 가 아니라 "내가 요즘 뭘
 * 담았나" 이고, 같은 카드가 두 컬렉션에 있어도 서버가 한 장으로 접어서 준다.
 */
export function fetchRecentCollectionCards(limit: number): Promise<CollectionGroupCard[]> {
  return apiGet<CollectionGroupCard[]>(`/collection/groups/cards/recent?limit=${limit}`);
}

export function fetchCollectionGroup(groupSerialNo: number): Promise<CollectionGroupDetail> {
  return apiGet<CollectionGroupDetail>(`/collection/groups/${groupSerialNo}`);
}

export function createCollectionGroup(name: string): Promise<CollectionGroupItem> {
  return apiPost<CollectionGroupItem>("/collection/groups", { name });
}

export function renameCollectionGroup(
  groupSerialNo: number,
  name: string,
): Promise<CollectionGroupItem> {
  return apiPut<CollectionGroupItem>(`/collection/groups/${groupSerialNo}`, { name });
}

export function deleteCollectionGroup(groupSerialNo: number): Promise<void> {
  return apiDelete(`/collection/groups/${groupSerialNo}`);
}

/** 이미 담겨 있으면 서버가 조용히 성공시킨다(멱등) — 호출부가 중복을 걱정할 필요 없다. */
export function addCardToGroup(groupSerialNo: number, cardSerialNo: number): Promise<void> {
  return apiPost<void>(`/collection/groups/${groupSerialNo}/cards`, { cardSerialNo });
}

export function removeCardFromGroup(
  groupSerialNo: number,
  cardSerialNo: number,
): Promise<void> {
  return apiDelete(`/collection/groups/${groupSerialNo}/cards/${cardSerialNo}`);
}
